import React, { JSX, useState } from 'react'
import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { AnalyticsMetafield, TimeSeriesTotals } from 'types/api'
import { cleanVariables, createTooltipFormatter, graphqlRequest, shortDateEpochFormatter } from 'lib/helpers'
import { EmptyDataCard } from 'components/EmptyDataCard'
import { useMediaQuery } from 'react-responsive'
import { RefreshingBlock } from './Loading'
import getGraphTimeseries from '../graphql/getGraphTimeseries.graphql'
import { useQuery } from '@tanstack/react-query'

const calculateInitialMin = (max: number, proportion: number): number =>
  proportion > 0 ? (max * -proportion) / (1 - proportion) : 0

const calculateInitialSecondaryMin = (max: number, proportion: number): number =>
  !isNaN(proportion) && proportion !== 1 ? (max * -proportion) / (1 - proportion) : 0

export interface ChartProps {
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  dataKey: string
  secondaryDataKey: string
  formatter: (value: number) => string
  secondaryFormatter: (value: number) => string
  width?: number | string
  height?: number | string
  token?: string
  client: AnalyticsMetafield | null
}

export const Chart = ({ token, client, filters, dataKey, secondaryDataKey, formatter, secondaryFormatter, width = '100%', height = 400 }: ChartProps): JSX.Element => {
  const oneHourInSeconds = 60 * 60
  const oneDayInSeconds = oneHourInSeconds * 24
  const {
    data,
    isFetching,
    isError
  } = useQuery({
    queryKey: ['dashboard', 'graphTimeSeries', filters, client?.id],

    queryFn: async () => {
      if (token == null) return

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filters?.productOption?.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate,
        binSize: oneDayInSeconds
      })
      const { timeSeriesTotals } = await graphqlRequest<{ timeSeriesTotals: TimeSeriesTotals[] }>(
        getGraphTimeseries, cleanVar, token
      )
      const finalTimeSeries = (timeSeriesTotals.length > 0) ? timeSeriesTotals : null

      return finalTimeSeries
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const isMobile = useMediaQuery({ maxWidth: 639 })

  const getMarginByLength = (length: number, isMobile: boolean): number => {
    const scales = [
      { max: 6, mobile: 5, desktop: 10 },
      { max: 7, mobile: 10, desktop: 15 },
      { max: 9, mobile: 15, desktop: 20 },
      { max: 10, mobile: 20, desktop: 25 }
    ]
    const scale = scales.find(s => length <= s.max) ?? { mobile: 30, desktop: 35 }
    return isMobile ? scale.mobile : scale.desktop
  }

  const getMaxFormattedLength = (dataKey: string, formatter: (value: number) => string): number => {
    if (data == null) return 0
    const maxValue = Math.max(...data.map(d => (d[dataKey] as number) ?? 0))
    return formatter(maxValue).length
  }

  const getLeftMargin = (): number => {
    const length = getMaxFormattedLength(dataKey, formatter)
    if (length > 0) return getMarginByLength(length, isMobile)
    return isMobile ? 15 : 25
  }

  const getRightMargin = (): number => {
    const length = getMaxFormattedLength(secondaryDataKey, secondaryFormatter)
    return length * (isMobile ? 1 : 2)
  }

  const [visible, setVisible] = useState({
    [dataKey]: true,
    [secondaryDataKey]: true
  })
  const handleLegendClick = (e: any): void => {
    const { dataKey } = e
    setVisible((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }))
  }

  const roundUpToNearest10 = (number: number): number => {
    if (number === 0) return 0
    return Math.ceil(number / 10) * 10
  }

  const roundDownToNearest10 = (number: number): number => {
    if (number === 0) return 0
    return -Math.ceil(Math.abs(number) / 10) * 10
  }

  const getAlignedDomains = (salesData: number[], quantityData: number[]): {
    dataDomain: number[]
    secondaryDataDomain: number[]
  } => {
    const dataMin = Math.min(0, ...salesData)
    const dataMax = Math.max(0, ...salesData)
    const secondaryDataMin = Math.min(0, ...quantityData)
    const secondaryDataMax = Math.max(0, ...quantityData)

    if (dataMax === 0 && dataMin === 0) {
      return { dataDomain: [0, 1], secondaryDataDomain: [0, 1] }
    }

    const dataRange = dataMax - dataMin
    const secondaryRange = secondaryDataMax - secondaryDataMin
    const dataNegativeProportion = dataRange > 0 ? Math.abs(dataMin / dataRange) : 0
    const secondaryNegativeProportion = secondaryRange > 0 ? Math.abs(secondaryDataMin / secondaryRange) : 0
    const dominantNegativeProportion = Math.max(dataNegativeProportion, secondaryNegativeProportion)

    const initialDataMin = calculateInitialMin(dataMax, dominantNegativeProportion)
    const initialSecondaryMin = calculateInitialSecondaryMin(secondaryDataMax, dominantNegativeProportion)

    const niceDataMax = roundUpToNearest10(dataMax)
    const niceDataMin = roundDownToNearest10(initialDataMin)

    const positiveScaleFactor = dataMax > 0 ? niceDataMax / dataMax : 1
    const negativeScaleFactor = initialDataMin < 0 ? niceDataMin / initialDataMin : 1

    const dominantScaleFactor = Math.max(positiveScaleFactor, negativeScaleFactor)

    const finalDataMax = dataMax * dominantScaleFactor
    const finalDataMin = initialDataMin * dominantScaleFactor

    const finalSecondaryMax = secondaryDataMax * dominantScaleFactor
    const finalSecondaryMin = initialSecondaryMin * dominantScaleFactor

    return {
      dataDomain: [finalDataMin, finalDataMax],
      secondaryDataDomain: [finalSecondaryMin, finalSecondaryMax]
    }
  }

  const { dataDomain, secondaryDataDomain } = Array.isArray(data) && data.length > 0
    ? getAlignedDomains(
      data.map(d => d[dataKey] ?? 0),
      data.map(d => d[secondaryDataKey] ?? 0)
    )
    : { dataDomain: [0, 0], secondaryDataDomain: [0, 0] }

  const tooltipFormatter = createTooltipFormatter(dataKey, secondaryDataKey, formatter, secondaryFormatter)

  const withData = (

    <ResponsiveContainer
      className='
    [--grid:#D3D3D3]
    [--axis:theme(colors.gray.500)]
    [--tooltipBg:var(--color-lm-bg-main)]
    [--tooltipText:#444b4c]
    [--bar:var(--color-lm-text-secondary)]
    [--barNeg:#8b2c2c]
    [--accent:var(--color-brand)]

    dark:[--grid:rgba(255,255,255,0.06)]
    dark:[--axis:rgba(255,255,255,0.45)]
    dark:[--tooltipBg:var(--color-tooltip)]
    dark:[--tooltipText:rgba(255,255,255,0.85)]
    dark:[--bar:rgba(252,180,21,0.28)]
    dark:[--barNeg:#8b2c2c]
    dark:[--accent:var(--color-brand)]
  ' width={width} height={height}
    >
      <ComposedChart
        data={data ?? undefined}
        margin={{ top: 10, right: getRightMargin(), left: getLeftMargin(), bottom: 0 }}
        data-testid='analytics-line-chart'
      >
        <CartesianGrid stroke='var(--grid)' strokeDasharray='3 3' horizontal vertical />
        <ReferenceLine
          yAxisId='right'
          y={0}
          stroke='var(--bar)'
          strokeWidth={2}
          strokeDasharray='3 3'
        />
        <XAxis
          data-testid='x-axis'
          dataKey='timestamp'
          domain={['dataMin', 'dataMax']}
          interval='preserveStartEnd'
          axisLine={false}
          tickFormatter={shortDateEpochFormatter}
          tickMargin={12}
          tickLine={false}
          tick={{ fontSize: isMobile ? 12 : 14 }}
        />
        <YAxis
          tickCount={6}
          data-testid='y-axis-left'
          yAxisId='left'
          interval='preserveStartEnd'
          domain={dataDomain}
          tick={{ fontSize: isMobile ? 12 : 14 }}
          allowDecimals={false}
          tickMargin={5}
          tickFormatter={formatter}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tickCount={6}
          data-testid='y-axis-right'
          yAxisId='right'
          orientation='right'
          interval='preserveStartEnd'
          domain={secondaryDataDomain}
          tick={{ fontSize: isMobile ? 12 : 14 }}
          allowDecimals={false}
          tickMargin={5}
          tickFormatter={secondaryFormatter}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          data-testid='tooltip'
          isAnimationActive={false}
          labelFormatter={shortDateEpochFormatter}
          cursor={false}
          separator=': '
          contentStyle={{
            borderRadius: 6,
            backgroundColor: 'var(--tooltipBg)',
            borderColor: 'var(--tooltipBg)',
            padding: '5px 10px'
          }}
          labelStyle={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: 0.1
          }}
          formatter={tooltipFormatter}
          itemStyle={{
            fontSize: 14,
            color: 'var(--tooltipText)',
            padding: 0
          }}
        />
        <Legend
          data-testid='legend'
          wrapperStyle={{
            fontSize: isMobile ? '12px' : '14px',
            textTransform: 'capitalize',
            paddingTop: '8px'
          }}
          onClick={handleLegendClick}
        />
        <Bar
          data-testid={`bar-${secondaryDataKey}`}
          yAxisId='right'
          dataKey={secondaryDataKey}
          fill='var(--bar)'
          barSize={isMobile ? 10 : 16}
          hide={!visible[secondaryDataKey]}
        >{data?.map((entry) => (
          <Cell key={`cell-${entry.timestamp}`} fill={entry[secondaryDataKey] < 0 ? 'var(--barNeg)' : 'var(--bar)'} />
        ))}
        </Bar>
        <Line
          data-testid={`line-${dataKey}`}
          yAxisId='left'
          dot={false}
          type='linear'
          dataKey={dataKey}
          stroke='var(--accent)'
          strokeWidth={2}
          isAnimationActive={false}
          hide={!visible[dataKey]}
        />
      </ComposedChart>
    </ResponsiveContainer>

  )

  const noData = <EmptyDataCard />

  return (
    <div data-testid='analytics-chart-wrapper' className='aspect-square md:aspect-[9/3] relative'>
      {isFetching && RefreshingBlock}
      {(data !== null || isError) ? withData : noData}
    </div>
  )
}
